import { workspaceDataStatusSchema } from "@adpilot/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { api } from "@/lib/api";
import { subscribeToDataStatus } from "@/lib/data-status-events";
import { SSE_FALLBACK_POLL_MS, SSE_SAFETY_POLL_MS } from "@/lib/sse-invalidation";
import { useAuthStore } from "@/stores/auth";

export const workspaceDataStatusKey = (workspaceId: string | null | undefined) =>
  ["sync", "data-status", workspaceId] as const;

export function useWorkspaceDataStatus(workspaceId: string | null | undefined) {
  const queryClient = useQueryClient();
  const previousSuccess = useRef(new Map<string, string | null>());
  const [sseFailed, setSseFailed] = useState(false);
  const query = useQuery({
    queryKey: workspaceDataStatusKey(workspaceId),
    queryFn: async () => {
      const response = await api
        .get("sync/data-status", {
          searchParams: { workspaceId: workspaceId! },
        })
        .json();
      return z.object({ success: z.literal(true), data: workspaceDataStatusSchema }).parse(response)
        .data;
    },
    enabled: Boolean(workspaceId),
    staleTime: 15_000,
    refetchInterval: sseFailed ? SSE_FALLBACK_POLL_MS : SSE_SAFETY_POLL_MS,
    refetchIntervalInBackground: false,
  });

  useEffect(() => {
    if (workspaceId)
      return subscribeToDataStatus(
        queryClient,
        workspaceId,
        ["sync", "data-status", workspaceId],
        (state) => setSseFailed(state === "error"),
      );
  }, [queryClient, workspaceId]);

  useEffect(() => {
    if (workspaceId) previousSuccess.current.clear();
  }, [workspaceId]);

  useEffect(() => {
    if (!query.data) return;
    const auth = useAuthStore.getState();
    if (auth.user?.activeWorkspaceId !== workspaceId) return;
    const names = new Map(query.data.shops.map((shop) => [shop.shopId, shop.shopName]));
    if (
      auth.shops.some(
        (shop) =>
          shop.workspaceId === workspaceId &&
          names.has(shop.shopId) &&
          names.get(shop.shopId) !== shop.shopName,
      )
    ) {
      auth.setShops(
        auth.shops.map((shop) =>
          shop.workspaceId === workspaceId && names.has(shop.shopId)
            ? { ...shop, shopName: names.get(shop.shopId)! }
            : shop,
        ),
      );
    }
    for (const shop of query.data.shops) {
      for (const source of shop.sources) {
        const key = `${shop.shopId}:${source.source}`;
        const previous = previousSuccess.current.get(key);
        previousSuccess.current.set(key, source.lastSuccessAt);
        if (previous === undefined || previous === source.lastSuccessAt || !source.lastSuccessAt)
          continue;
        // Background completion refreshes the data being used without interrupting the user.
        if (["catalog", "inventory"].includes(source.source)) {
          void queryClient.invalidateQueries({ queryKey: ["product-listings"] });
          void queryClient.invalidateQueries({ queryKey: ["product-center"] });
        } else if (source.source === "store_info") {
          void queryClient.invalidateQueries({ queryKey: ["amazon"] });
        } else {
          void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
          void queryClient.invalidateQueries({ queryKey: ["product-center"] });
        }
      }
    }
  }, [query.data, queryClient, workspaceId]);
  return query;
}
